/*
 * @description:
 * @author: Jason Wong
 * @since: 2024-07-12 15:54:58
 */

import { get, isArray, set } from "lodash";
import {
  ROLES_KEY,
  TENANT_ID,
  TENANT_LIST_ID,
  TOKEN_KEY,
  USER_INFO_KEY,
  DB_DICT_DATA_KEY,
} from "../../enums/cacheEnum";
import { getAuthCache, getToken, setAuthCache } from "../../utils/auth";
import { doLogout, getRoles, getUserInfo, loginApi } from "../../api/account";
import { getProductConfigCatVO } from "../../api/tenant";
import router from "../../router";
import Vuex from "vuex";
import { Persistent } from "../../utils/cache/persistent";

const RECENTLY_USED_LANG_MAX_LENGTH = 3;
const EMPTY_TOKEN_PLACE_HOLDER = "$_$_empty_$_$";

export const userState = {
  notLogined: "notLogined",
  logining: "logining",
  logined: "logined",
  invalid: "invalid",
};

const useUserStore = {
  state: {
    userInfo: null,
    token: EMPTY_TOKEN_PLACE_HOLDER,
    roleList: [],
    tenantid: null,
    tenantList: [],
    recentlyUsedLanguages: [],
    productConfig : null,
  },
  mutations: {
    setUserInfo(state, userInfo) {
      state.userInfo = userInfo;
      setAuthCache(USER_INFO_KEY, userInfo);
    },
    setToken(state, token) {
      state.token = token;
      setAuthCache(TOKEN_KEY, token);
    },
    setRoleList(state, roleList) {
      state.roleList = roleList;
      setAuthCache(ROLES_KEY, roleList);
    },
    setTenantId(state, tenantid) {
      state.tenantid = tenantid;
      setAuthCache(TENANT_ID, tenantid);
    },
    setTenantList(state, tenantList) {
      state.tenantList = tenantList;
    },
    addUsedLanguage(state, lang) {
      const pos = state.recentlyUsedLanguages.indexOf(lang);
      pos > -1 && state.recentlyUsedLanguages.splice(pos, 1);
      state.recentlyUsedLanguages.unshift(lang);
      if (state.recentlyUsedLanguages.length > RECENTLY_USED_LANG_MAX_LENGTH) {
        state.recentlyUsedLanguages.pop();
      }
      state.recentlyUsedLanguages = [...state.recentlyUsedLanguages];
    },
    setUsedLanguages(state, langs) {
      state.recentlyUsedLanguages = langs || [];
    },
    setProductConfig(state,productConfig){
      state.productConfig = productConfig;
    }
  },
  actions: {
    async loginAction({ commit, dispatch }, data) {
      try {
        const { token, userInfo } = data;
        commit("setToken", token);
        commit("setTenantId", userInfo.loginTenantId);
        return dispatch("afterLoginAction", data);
      } catch (err) {
        return Promise.reject(err);
      }
    },
    async refresh({ commit, dispatch, state }, data) {
      try {
        return dispatch("afterLoginAction");
      } catch (err) {
        return Promise.reject(err);
      }
    },
    async afterLoginAction({ commit, dispatch, state, getters }, data) {
      console.log('#-# after login action...');
      if (!getters.getToken || !getters.getTenantId) return;
      const userInfo = await dispatch("getUserInfoAction");
      await dispatch("getProductConfigAction");
      const redirect = router.currentRoute.query.redirect;
      if (!!redirect) {
        window.open(redirect, "_self");
      }
      return userInfo;
    },
    async getUserInfoAction({ commit, dispatch, state }) {
      if (!state.token) return null;
      let userInfo, tenants;
      try {
        const ress = await getUserInfo();
        userInfo = ress.userInfo;
        tenants = ress.tenants;

        if (state.tenantid == null) {
          state.tenantid = getAuthCache(TENANT_ID);
        }
        if (tenants.filter((i) => i.id == state.tenantid).length == 0) {
          throw new Error("租户信息已失效");
        }
      } catch (err) {
        console.log(err);
        throw err;
      }
      const roles = await getRoles();
      commit(
        "setRoleList",
        roles.map((item) => item.roleCode)
      );
      commit("setTenantList", tenants);
      commit("setUserInfo", userInfo);
    },
    async getProductConfigAction({ commit, dispatch, state }) {
      if (!state.token) return null;
      let productConfig = null;
      try {
        let param = {
          tenantId: parseInt(state.tenantid),
        };
        const ress = await getProductConfigCatVO(param);
        productConfig = ress;
      } catch (err) {
        console.log(err);
        throw err;
      }
      commit("setProductConfig", productConfig);
    },
    switchTenantAction({ commit, dispatch, state }, tenantid) {
      commit("setTenantId", tenantid);
    },

    async clearAll({ commit, dispatch, state }) {
      commit("setToken", null);
      commit("setTenantId", null);
      commit("setUserInfo", null);
      commit("setRoleList", null);
      commit("setTenantList", null);
      commit("setProductConfig",null);
      Persistent.clearAll(true);
    },
    async logout({ commit, dispatch, state }, { goLogin = false }) {
      if (!!getToken()) {
        try {
          await doLogout();
        } catch {
          console.log("注销Token失败");
        }
      }

      commit("setToken", null);
      commit("setUserInfo", null);
      commit("setRoleList", null);
      commit("setTenantList", null);
      commit("setProductConfig",null);
      goLogin && router.push({ name: "login" });
    },
    addUsedLanguage({ commit }, lang) {
      commit("addUsedLanguage", lang);
    },
    setUsedLanguages({ commit }, langs) {
      commit("setUsedLanguages", langs);
    },
  },
  getters: {
    getUserInfo(state) {
      if (!!state.userInfo) {
        state.userInfo = getAuthCache(USER_INFO_KEY);
        //TODO 教学模块调试(p.s. 后期可能没有模块权限)
        state.userInfo.systemCodes = "1teaching";
      }
      return state.userInfo;
    },
    getToken(state) {
      if (!state.token || state.token === EMPTY_TOKEN_PLACE_HOLDER) {
        state.token = getAuthCache(TOKEN_KEY);
      }
      return state.token;
    },
    isTokenValid(state) {
      if (!state.token || state.token === EMPTY_TOKEN_PLACE_HOLDER) return true;
      return state.token == getAuthCache(TOKEN_KEY);
    },
    isPageUserLoadded(state) {
      return !!state.userInfo;
    },
    getPageToken(state) {
      return state.token;
    },
    getRoleList(state) {
      if (state.roleList.length == 0) {
        state.roleList = getAuthCache(ROLES_KEY);
      }
      return state.roleList;
    },
    getTenantId(state) {
      if (state.tenantid == null) {
        state.tenantid = getAuthCache(TENANT_ID);
      }
      return state.tenantid;
    },
    getTenants(state) {
      return state.tenantList;
    },
    getCurrentTenant(state) {
      if (!state.tenantList || state.tenantList.length == 0 || state.tenantid == null) return null;
      return state.tenantList.filter((i) => i.id == state.tenantid)[0];
    },
    getProductConfig(state){
      return state.productConfig;
    }
  },
};

export default useUserStore;
